import { pluck } from '@/helpers/object-helper';
import Provider from '@/interfaces/provider';
import { FreezingItems } from '@/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import {
	ProviderType,
	ProviderTypeToMonetizationTypes,
	ProviderTypeWithMonetizationTypes,
} from '@/components/filter-bar/filter-bar.types';

export type ConstantState = {
	providers: Provider[];
	upcomingProviders: Set<number>;
};

type ConstantGetter<T> = (state: ConstantState) => T;

export type ConstantGetters = {
	providers: ConstantGetter<Provider[]>;
	providersById: ConstantGetter<Record<string, Provider>>;
	providersByShortName: ConstantGetter<Record<string, Provider>>;
	providersByType: ConstantGetter<Record<ProviderType, Provider[]>>;
};

export type ConstantStore = {
	state: ConstantState;
	getters: ConstantGetters;
};

export enum ConstantType {
	PROVIDERS = 'providers',
}

export const CONSTANT_INIT_FREEZE_ITEMS: FreezingItems<ConstantState> = {
	providers: state => {
		(['providers'] as (keyof ConstantState)[]).map(key => state[key]).forEach((item: any) => Object.freeze(item));
	},
};

const state = () =>
	({
		providers: [],
		upcomingProviders: new Set(),
	} as ConstantState);

// GETTERS
const getters: GetterTree<ConstantState, any> = {
	/**
	 * visible providers for the provider filter bar.
	 */

	allProviders:
		(state, getters, rootState, rootGetters) =>
		(filterByUserProviders = true) => {
			let filteredProviders = state.providers || [];
			let { providers: userProviders = [] } = rootState.user.settings;
			// cheap trick for the wrong serialization userProviders on the server is an object instad of an array
			// loggedInProviders is also affected
			if (!(userProviders instanceof Array)) {
				userProviders = Object.values(userProviders);
			}

			if (filterByUserProviders && userProviders.length) {
				filteredProviders = filteredProviders.filter(provider => userProviders.includes(provider.shortName));
			}
			return filteredProviders;
		},
	allProvidersById: (state, getters) => {
		return pluck(getters.allProviders(false), 'packageId');
	},
	providers:
		(state, getters, rootState, rootGetters) =>
		(filterByUserProviders = true) => {
			if (!rootState.routing.activeRoute?.name?.startsWith('app.sports')) {
				return getters.allProviders(filterByUserProviders).filter((p: Provider) => p.hasTitles);
			}

			return getters.sportProviders;
		},
	sportProviders(state): Provider[] {
		return state.providers.filter(p => p.hasSport);
	},
	titleProviders(state): Provider[] {
		return state.providers.filter(p => p.hasTitles);
	},
	titleProviderCount(state, getters): number {
		return getters.titleProviders.length;
	},
	sportProvidersById(state, getters): Record<number, Provider> {
		return pluck(getters.sportProviders, 'packageId');
	},
	sportProvidersByShortName(state, getters): Record<string, Provider> {
		return pluck(getters.sportProviders, 'shortName');
	},

	titleProvidersById(state, getters): Record<number, Provider> {
		return pluck(getters.titleProviders, 'packageId');
	},
	titleProvidersByShortName(state, getters): Record<string, Provider> {
		return pluck(getters.titleProviders, 'shortName');
	},
	providersById(state, getters, rootState): Record<number, Provider> {
		if (!rootState.routing.activeRoute?.name?.startsWith('app.sports')) {
			return getters.titleProvidersById;
		}
		return getters.sportProvidersById;
	},
	providersByShortName(state, getters, rootState): Record<string, Provider> {
		if (!rootState.routing.activeRoute?.name?.startsWith('app.sports')) {
			return getters.titleProvidersByShortName;
		}
		return getters.sportProvidersByShortName;
	},
	providersByType(state, getters) {
		return getters
			.providers(false)
			.reduce((providersByType: Record<ProviderType, Provider[]>, provider: Provider) => {
				const providerType = Object.keys(ProviderTypeToMonetizationTypes).find(providerType =>
					provider.monetizationTypes?.find(mt =>
						ProviderTypeToMonetizationTypes[providerType as ProviderTypeWithMonetizationTypes].includes(mt)
					)
				) as ProviderType;

				if (!providerType) {
					return providersByType;
				}

				return {
					...providersByType,
					[providerType]: [...(providersByType[providerType] || []), provider],
				};
			}, {} as Record<ProviderType, Provider[]>);
	},
	/**
	 * get first three providers of the region to preselect for a new user
	 */
	preselectedProviders: (state, getters, rootState): string[] => {
		const { providers: userProviders = [] } = rootState.user.settings;
		return userProviders.length > 0
			? userProviders
			: (getters.providers(false) as Provider[]).slice(0, 3).map(provider => provider.shortName);
	},
};

// ACTIONS
const actions: ActionTree<ConstantState, any> = {
	async fetchPackages({ commit, rootGetters }) {
		const query = `query GetPackages($country: Country!, $platform: Platform! = WEB) {
			packages(country: $country, platform: $platform, includeAddons: true) {
				clearName
				hasSport(country: $country, platform: $platform)
				hasTitles(country: $country, platform: $platform)
				icon
				monetizationTypes
				packageId
				selected
				shortName
				slug
				technicalName
			}
		}`;

		const variables = {
			country: rootGetters['language/country'],
		};

		const headers = {
			'Content-Type': 'application/json',
		};

		const res = await fetch(JW_CONFIG.GRAPHQL_URL, {
			method: 'POST',
			headers,
			body: JSON.stringify({ query, variables }),
		});
		const { packages = [] } = (await res.json())?.data;
		commit('SET_PACKAGES', packages);
	},
};

// MUTATIONS
const mutations: MutationTree<ConstantState> = {
	SET_PACKAGES(state, providers: Provider[]) {
		const items = [...providers].map(item => Object.freeze(item));
		state.providers = items;
	},
	SET_UPCOMING_PROVIDERS(state, providerIds: Set<number>) {
		state.upcomingProviders = providerIds;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<ConstantState, any>;
